import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import MaintenanceDialogTitle from './MaintenanceDialogTitle';
import {
    isValidSiteReference,
    normaliseSiteReference,
    siteReferenceHelperText
} from './SiteReference';

interface Props {
    open: boolean;
    onClose: () => void;
    onDeleted: () => void;
}

type Version = { id: number; versionName: string; start: string };
type VersionsResponse = { availableVersions: Version[]; latestVersionId: number };
type SiteDeletionPreview = {
    reference: string;
    versionId: number;
    versionName: string;
    description: string;
    townCity: string;
    surveyDayCount: number;
    developmentUnitCount: number;
    totalRecordCount: number;
};

export default function DeleteSiteDialog({ open, onClose, onDeleted }: Props) {
    const { get, post, showMessage } = useContext(APIContext);
    const [versions, setVersions] = useState<Version[]>([]);
    const [versionId, setVersionId] = useState(0);
    const [reference, setReference] = useState('');
    const [preview, setPreview] = useState<SiteDeletionPreview | null>(null);
    const [confirmationReference, setConfirmationReference] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setReference('');
        setPreview(null);
        setConfirmationReference('');
        setConfirming(false);
        setSubmitting(false);
    };

    useEffect(() => {
        if (!open) return;
        reset();
        get('Admin', 'GetSiteReferenceVersions', (response: VersionsResponse) => {
            setVersions(response.availableVersions);
            setVersionId(response.latestVersionId);
        });
    }, [open]);

    const close = () => {
        if (submitting) return;
        reset();
        onClose();
    };

    const loadPreview = async () => {
        setPreview(null);
        await post('Admin', 'PreviewSiteDeletion', {
            reference: normaliseSiteReference(reference),
            confirmationReference: '',
            versionId
        }, setPreview);
    };

    const openConfirmation = () => {
        setConfirmationReference('');
        setConfirming(true);
    };

    const deleteSite = async () => {
        if (!preview) return;
        setSubmitting(true);
        await post('Admin', 'DeleteSite', {
            reference: preview.reference,
            confirmationReference: normaliseSiteReference(confirmationReference),
            versionId: preview.versionId
        }, () => {
            showMessage('success', `${preview.reference} was deleted from ${preview.versionName}.`);
            onDeleted();
            reset();
            onClose();
        });
        setSubmitting(false);
    };

    const referenceChanged = (value: string) => {
        setReference(value.toUpperCase());
        setPreview(null);
    };

    const canDelete = !!preview &&
        normaliseSiteReference(confirmationReference) === preview.reference &&
        !submitting;

    return <>
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
            <MaintenanceDialogTitle
                icon={<DeleteForeverOutlinedIcon />}
                title="Delete a site"
                description="Permanently remove one site from one data version"
                tone="error"
            />
            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    <Alert severity="warning">
                        Deletion cannot be undone. The same site in other data versions will not be changed.
                    </Alert>
                    <TextField
                        select
                        fullWidth
                        label="Data version"
                        value={versionId}
                        disabled={submitting || versions.length === 0}
                        onChange={event => {
                            setVersionId(Number(event.target.value));
                            setPreview(null);
                        }}
                    >
                        {versions.map(version => <MenuItem key={version.id} value={version.id}>
                            {version.versionName}
                        </MenuItem>)}
                    </TextField>
                    <TextField
                        fullWidth
                        autoFocus
                        label="Site reference"
                        placeholder="AA-01-A-01"
                        value={reference}
                        disabled={submitting}
                        onChange={event => referenceChanged(event.target.value)}
                        onBlur={() => isValidSiteReference(reference) &&
                            setReference(normaliseSiteReference(reference))}
                        error={reference.length > 0 && !isValidSiteReference(reference)}
                        helperText={siteReferenceHelperText}
                    />
                    {preview && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                        <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                                <Box>
                                    <Typography variant="overline" color="text.secondary">Site found</Typography>
                                    <Typography variant="h6">{preview.reference}</Typography>
                                </Box>
                                <Chip label={preview.versionName} color="primary" variant="outlined" />
                            </Stack>
                            <Typography>
                                {preview.description || 'No site description'}
                                {preview.townCity ? `, ${preview.townCity}` : ''}
                            </Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Chip size="small" label={`${preview.surveyDayCount} survey day${preview.surveyDayCount === 1 ? '' : 's'}`} />
                                <Chip size="small" label={`${preview.developmentUnitCount} development unit${preview.developmentUnitCount === 1 ? '' : 's'}`} />
                                <Chip size="small" color="error" variant="outlined" label={`${preview.totalRecordCount} records to remove`} />
                            </Stack>
                        </Stack>
                    </Paper>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={close} disabled={submitting}>Cancel</Button>
                {preview
                    ? <Button variant="contained" color="error" onClick={openConfirmation}>
                        Continue to deletion
                    </Button>
                    : <Button
                        variant="contained"
                        onClick={loadPreview}
                        disabled={!versionId || !isValidSiteReference(reference) || submitting}
                    >
                        Review site
                    </Button>}
            </DialogActions>
        </Dialog>

        <Dialog
            open={confirming}
            onClose={() => !submitting && setConfirming(false)}
            maxWidth="sm"
            fullWidth
        >
            <MaintenanceDialogTitle
                icon={<ReportProblemOutlinedIcon />}
                title="Confirm permanent deletion"
                description="One final check before the site is removed"
                tone="error"
            />
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="error">
                        You are about to permanently delete <strong>{preview?.reference}</strong> and all of its
                        records from <strong>{preview?.versionName}</strong>.
                    </Alert>
                    <Typography variant="body2">
                        To double-check, type <strong>{preview?.reference}</strong> below.
                    </Typography>
                    <TextField
                        fullWidth
                        autoFocus
                        label="Confirm site reference"
                        value={confirmationReference}
                        disabled={submitting}
                        onChange={event => setConfirmationReference(event.target.value.toUpperCase())}
                        onBlur={() => isValidSiteReference(confirmationReference) &&
                            setConfirmationReference(normaliseSiteReference(confirmationReference))}
                        error={confirmationReference.length > 0 &&
                            (!isValidSiteReference(confirmationReference) ||
                                normaliseSiteReference(confirmationReference) !== preview?.reference)}
                        helperText={siteReferenceHelperText}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={() => setConfirming(false)} disabled={submitting}>Go back</Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={deleteSite}
                    disabled={!canDelete}
                    startIcon={<DeleteForeverOutlinedIcon />}
                >
                    Delete site
                </Button>
            </DialogActions>
        </Dialog>
    </>;
}
